import { ArrowUp, ArrowDown } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { MetricDef } from '../metric-defs';
import type { ScalarMetricResult } from '@/types/analyticsV2';
import { METRIC_BORDER, NEUTRAL_BORDER } from './metric-accents';

const formatTrend = (n: number) => {
  const abs = Math.abs(n);
  return Number.isInteger(abs) ? `${abs}%` : `${abs.toFixed(1)}%`;
};

interface MetricStatProps {
  def: MetricDef;
  result: ScalarMetricResult | undefined;
  isLoading: boolean;
}

export function MetricStat({ def, result, isLoading }: MetricStatProps) {
  const border = METRIC_BORDER[def.key] ?? NEUTRAL_BORDER;

  let value = '—';
  let denominator: string | null = null;
  let trend: number | null = null;
  if (result) {
    trend = result.trend[def.trendField];
    if (def.cardDisplay === 'count-with-denominator') {
      value = result.current.count.toLocaleString();
      denominator = result.current.total.toLocaleString();
    } else {
      value = result.current.percent === null ? '—' : `${result.current.percent}%`;
    }
  }
  const showTrend = !isLoading && trend !== null && trend !== undefined;
  const isUp = (trend ?? 0) >= 0;

  return (
    <div className={cn('flex flex-col gap-0.5 border-l-2 pl-2.5', border)}>
      <span className='truncate text-[11px] font-medium text-gray-700'>{def.label}</span>
      {isLoading ? (
        <Skeleton className='h-4 w-14 rounded' />
      ) : (
        <div className='flex items-baseline gap-1'>
          <span className='text-[15px] font-semibold leading-none text-gray-900 tabular-nums'>{value}</span>
          {denominator && <span className='text-[11px] text-gray-500 tabular-nums'>/{denominator}</span>}
          {showTrend && (
            <span
              className={cn(
                'ml-auto inline-flex items-center gap-0.5 text-[10px] font-medium tabular-nums',
                isUp ? 'text-emerald-600' : 'text-rose-500',
              )}
            >
              {isUp ? <ArrowUp className='h-2.5 w-2.5' /> : <ArrowDown className='h-2.5 w-2.5' />}
              {formatTrend(trend ?? 0)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
